import * as Pusher from 'pusher-js';
import { eventChannel, delay } from 'redux-saga';
import { call, fork, put, select, take, takeEvery } from 'redux-saga/effects';
import * as actions from '../actions';
import * as api from '../actions/api';

import { getConvo,  getVisitor } from '../selectors/conversation';
import { getSettings } from '../selectors/settings';

import {
    BotConvoStartedAction,
    ConvoResumedAction,
    NewBotMessagePayload,
    TypingStatusChangeAction,
} from '../typedefs/actions';
import { BOT_CONVO_STARTED, TYPING_STATUS_CHANGE, CONVO_RESUMED } from '../typedefs/actionTypes';
import { ConversationState, SettingsState, VisitorState } from '../typedefs/store';
import { TranscriptMessage } from '../typedefs/api';

let client: Pusher.Pusher;
let subscription: any | void;

export function clearSubscription() {
    subscription = null;
}

function connectToPusher(key: string, authEndpoint: string, token: string) {
    return new Promise((resolve, reject) => {
        client = new Pusher(key, {
            auth: { headers: { 'X-Visitor-Token': token } },
            authEndpoint,
            encrypted: true,
        });

        client.connection.bind('connected', resolve);
    });
}

function mergeButtonsIntoTranscript(
    transcript: TranscriptMessage[],
    initialMessages: {buttons?: string[], text: string}[]
) {
    if (initialMessages) {
        for (const msg of initialMessages) {
            if (
                Array.isArray(msg.buttons) && msg.buttons.length > 0 &&
                transcript.find(({ body }) => (body === msg.text))
            ) {
                const idx = transcript.findIndex(({ body }) => (body === msg.text));
                if (Array.isArray(transcript[idx].buttons) && (transcript[idx].buttons as any[]).length > 0) {
                    transcript[idx].buttons = [...(transcript[idx].buttons as any[]), msg.buttons];
                } else {
                    transcript[idx].buttons = [...msg.buttons];
                }
            }
        }
    }

    return transcript;
}

function* connectAndSubscribe(action: BotConvoStartedAction | ConvoResumedAction) {
    const convo: ConversationState = yield select(getConvo);
    const { payload } = (action as BotConvoStartedAction);
    const settings: SettingsState = yield select(getSettings);
    const visitor: VisitorState = yield select(getVisitor);

    yield call(
        connectToPusher,
        settings.pusherKey,
        settings.inlineConfig.apiUrl + '/pusher/auth/visitor',
        visitor.visitorToken,
    );

    subscription = client.subscribe(convo.channel as string);

    const messages = yield call(api.getTranscript);

    if (convo.accepted) {
        yield put(actions.convoTranscriptFetched(messages.data.transcript));
    } else {
        yield call(
            addInitialMessages,
            mergeButtonsIntoTranscript(
                messages.data.transcript as TranscriptMessage[],
                payload && Array.isArray(payload.initialMessages) ? payload.initialMessages : [],
            )
        );
    }

    yield fork(watchPusherChannel, subscription);
}

export function* watchPusherChannel(subscription: Pusher.Channel) {
    const whitelistedEvents = [
        'agent-accepted',
        'bot-conversation-ended',
        'client-chat-status-notification',
        'client-video-request',
        'contact-info-gathered',
        'conversation-ended',
        'new-bot-message',
        'new-message',
        'redirect-to-url',
        'send-external-event',
        'sent-to-crm',
        'started-real-convo',
    ];

    const chan = eventChannel(emitter => {
        whitelistedEvents.forEach(event => {
            subscription.bind(event, (data: any) => emitter({ event, data }));
        });

        return () => {
            whitelistedEvents.forEach(event => {
                subscription.unbind(event);
            });
        };
    });
    while (true) {
        const { event, data } = yield take(chan);
        yield put({
            type: 'SOCKET_EVENT_' + event.toUpperCase().replace(/[\W_]+/g, '_'),
            payload: { channel: subscription.name, ...data },
        });
    }
}

export function getSubscription() {
    return subscription;
}

function* typingStatusChange(action: TypingStatusChangeAction) {
    const visitor: VisitorState = yield select(getVisitor);
    if (!visitor.hasConvo || !subscription) return;

    subscription.trigger('client-chat-status-notification', {
        status: action.payload.status,
        fromType: 'visitor',
        fromId: visitor.visitorId,
    });
}

function calculateTypingDuration(message: string) {
    const max = 1750;
    const calculated = message.length * 25;
    return calculated < max ? calculated : max;
}

export function* addInitialMessages(messages: TranscriptMessage[]) {
    const ignoreTypes = [
        'visitor-joined',
        'visitor-left',
        'agent-joined',
        'agent-ended',
        'visitor-url',
        'visitor-video-request',
        'visitor-ended',
    ];

    let i = 0;
    for (const message of messages) {
        if (ignoreTypes.includes(message.type)) {
            continue;
        }

        if (i > 0) {
            // if there are multiple messages from the bot being sent at the same time, add a slight delay between the
            // last message we sent and the next typing notification
            yield call(delay, 500);

            // simulate start typing, ...wait, then stop typing
            yield put(actions.setBotTyping(true));
            yield call(delay, calculateTypingDuration(message.body));
            yield put(actions.setBotTyping(false));
        }

        yield put(actions.newBotMessage({
            id: message.messageId,
            timestamp: message.timestamp,
            text: message.body,
            buttons: message.buttons,
            type: message.type,
        } as NewBotMessagePayload));
        i++;
    }
    return;
}

export default function* websocketSaga() {
    yield takeEvery([BOT_CONVO_STARTED, CONVO_RESUMED], connectAndSubscribe);
    yield takeEvery(TYPING_STATUS_CHANGE, typingStatusChange);
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/sagas/websocketSaga.ts